import { useState } from "react";
import { Flex, Text, ChartConfig } from "@suns/design-system";
import { MetricButton } from "./MetricButton";
import { MixedChart } from "@/components/MixedChart/MixedChart";
import { DataAreaChart } from "@/components";
import { formatMoney } from "@/shared/utils/helper-functions";
import { Timeframe } from "@/shared/const";
import dayjs from "dayjs";

interface YAxisConfig {
  minValue?: number;
  maxValue?: number;
  minTickCount?: number;
  customTick?:
    | React.ReactElement
    | ((props: {
        x: number;
        y: number;
        payload: { value: number };
      }) => React.ReactElement)
    | undefined;
}

interface MetricChartContainerProps {
  title: string;
  data: {
    snapshotDate: string;
    [key: string]: string | number | null;
  }[];
  config: ChartConfig;
  chartType: string | null;
  showFutureProjections: boolean;
  timeframe: Timeframe;
  activeChart: string;
  adjustedControls?: React.ReactNode;
  type: "composed chart" | "area chart" | "line chart";
  yAxisConfig?: YAxisConfig;
  print?: boolean;
}

const tooltipConfig = {
  prv: {
    formatter: (value: number) => formatMoney(value),
    relatedMetrics: [
      { key: "ppv", label: "PPV" },
      { key: "capHit", label: "Cap Hit" },
    ],
  },
};

export default function MetricChartContainer({
  title,
  data,
  config,
  chartType,
  showFutureProjections,
  timeframe,
  activeChart,
  adjustedControls,
  type,
  yAxisConfig,
  print = false,
}: MetricChartContainerProps) {
  type MetricKey = keyof typeof config;

  const [activatedChart, setActivatedChart] = useState<MetricKey | null>(
    activeChart
  );

  const onMetricClick = (metric: MetricKey) => {
    setActivatedChart(metric);
  };

  const getMetricValues = (
    data: {
      snapshotDate: string;
      [key: string]: string | number | null;
    }[],
    metric: MetricKey
  ) => {
    const filteredData = data
      .filter((metadata) => {
        const snapshotDate = metadata.snapshotDate;
        if (!snapshotDate) return true;
        return dayjs(snapshotDate).isBefore(dayjs());
      })
      .sort(
        (a, b) =>
          dayjs(b.snapshotDate).valueOf() - dayjs(a.snapshotDate).valueOf()
      );

    const currentValue =
      filteredData.find((d) => d[metric] !== null)?.[metric]?.toString() ??
      null;
    const previousValue =
      filteredData
        .find((d, index) => index > 0 && d[metric] !== null)
        ?.[metric]?.toString() ?? null;

    return {
      currentValue,
      previousValue,
    };
  };

  const filteredMetadata =
    showFutureProjections && activatedChart && type !== "area chart"
      ? data
      : data.filter((metadata) => {
          const snapshotDate = metadata.snapshotDate;
          if (!snapshotDate) return true;
          return dayjs(snapshotDate).isBefore(dayjs());
        });

  return (
    <Flex direction="down" gap="md" className="w-full">
      <Flex direction="right" justify="between">
        <Text heading muted>
          {title}
        </Text>
        {adjustedControls}
      </Flex>
      <Flex direction="down" gap="sm" className="w-full">
        <Flex direction="right" gap="sm" className="flex-wrap">
          {Object.entries(config)
            .filter(
              ([, config]) =>
                !config.isUpperBound &&
                !config.isLowerBound &&
                !config.isValueRange
            )
            .map(([key, config]) => {
              const values = getMetricValues(
                filteredMetadata,
                key as MetricKey
              );
              return (
                <MetricButton
                  key={key}
                  label={config.label as string}
                  color={config.color as string}
                  currentValue={values.currentValue}
                  differenceType={
                    chartType === "currency" ? "percentage" : null
                  }
                  active={activatedChart === key}
                  onClick={() => onMetricClick(key as MetricKey)}
                  disabled={type === "area chart"}
                  print={print}
                />
              );
            })}
        </Flex>
        {type === "composed chart" ? (
          <MixedChart
            timeframe={timeframe}
            chartData={filteredMetadata}
            chartConfig={config}
            activeChart={activatedChart}
            chartType={chartType as "currency" | null}
            showFutureProjections={showFutureProjections}
            yAxisConfig={yAxisConfig}
          />
        ) : (
          <DataAreaChart
            timeframe={timeframe}
            chartData={filteredMetadata}
            chartConfig={config}
            tooltipConfig={tooltipConfig}
          />
        )}
      </Flex>
    </Flex>
  );
}
